import { getCurrentInstance, ComponentInternalInstance, Ref, ref, reactive } from '@vue/composition-api';
import async from 'async';
import _ from 'lodash';
import { i18n_t } from '@/locales/i18n';
import guideStepHandler from '@/renderer/components/GuideAndHelp/composition/guideStepHandler';

// 引入工具类
import { IpcEvents } from '@/ipc-events';
import { PAY_SOURCE, openTeamPayDialog } from '@/renderer/scripts/composition/dialogHandler';
import ipc from '@/renderer/scripts/utils/ipc';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import { IS_BROWSER } from '@/common/constant/platform';
import smh from '@/renderer/scripts/smh';
import { FileInfo, TeamPathInfo } from '@/renderer/scripts/utils/upload/types';
import { Task } from '@/renderer/scripts/utils/TaskManager/types';
import uploadTaskManager, { handleUploadFilesToTask } from '@/renderer/scripts/utils/upload/index';
import storage from '@/renderer/scripts/utils/storage';
import errorCodeMap from '@/renderer/scripts/utils/errorCode';
import { getConcatDirPath } from '@/renderer/scripts/utils/transmission/fileUtils';
import { filesClassifyAdapter } from '@/renderer/scripts/utils/transmission/adapter/FileAdapter';
import { listFolders, isFinderAlias, FileStats } from '@/common/fastListFolder';
import { isLocalPathValid } from '@/common/fileUtils';
import ConstantConfig from '@/renderer/scripts/utils/ConstantConfig';
import openConflictDialog from './dialog';

export interface UploadFileRaw {
  webkitRelativePath: string;
  fullPath?: string;
  name: string;
  path: string;
  size: number;
  lastModified: number;
  isDir?: boolean;
  mtime?: number;
  folderSize?: number;
  relativePath?: string;
  folder?: string;
}

export interface UploadFile extends Blob {
  name: string;
  size: number;
  type: string;
  path: string;
  lastModified: number;
  fullPath?: string;
  webkitRelativePath?: string;
  raw?: UploadFileRaw;
}
type FailInfo = {
  list: unknown[];
  type: string;
};
let fs: typeof import('fs');
if (!IS_BROWSER) {
  fs = (window.parent as any).require('fs') as typeof import('fs');
}

// function filterErrorPaths(list: FileStats[]) {
//   return list
//     .filter(item => item.error)
//     .map(item => ({ path: item.path, error: item.error?.code, msg: errorCodeMap[item.error?.code as string] }));
// }

export default function handler() {
  const currComponent: ComponentInternalInstance = getCurrentInstance() as ComponentInternalInstance;
  const vm = currComponent.proxy;
  let routerName = vm.$route.name as string;
  const { methods: guideStepMethods } = guideStepHandler();
  const methods = {
    checkLocalPathInvalid(filePaths: string[]) {
      const failPaths: Array<{ path: string; code: string; msg: string }> = [];
      const validPaths: string[] = [];
      filePaths.forEach(path => {
        try {
          isLocalPathValid(path);
          const isAlias = isFinderAlias(path);
          if (isAlias) {
            throw { code: 'NotSuportFinderAlias' };
          }
          validPaths.push(path);
        } catch (error: any) {
          failPaths.push({
            path,
            code: error.code,
            msg: errorCodeMap[error.code],
          });
        }
      });
      return { validPaths, failPaths };
    },
    async openSelection(type: 'folder' | 'file' | 'files' | string, currPath: string, spaceId: string) {
      // 区分浏览器和客户端选择文件
      if (IS_BROWSER) {
        const { directoryInput, fileInput } = currComponent.refs;
        switch (type) {
          case 'file':
            (fileInput as HTMLElement).click();
            break;
          default:
            (directoryInput as HTMLElement).click();
            break;
        }
      } else if (type === 'file') {
        const { files } = await ipc.invoke(IpcEvents.INVOKE_OPERATE_MAIN_LISTENER, {
          event: 'selectFiles',
        });
        if (!files || !files.length) return;
        this.filesDeal(files, currPath, spaceId);
      } else {
        const options = {
          event: type === 'files' ? 'selectFilesAndFolders' : 'selectFolders',
          params: { noResolveAliases: true },
        };
        const { filePaths } = await ipc.invoke(IpcEvents.INVOKE_OPERATE_MAIN_LISTENER, options);
        if (!filePaths || !filePaths.length) return;

        const list = await this.listFolderInPC(filePaths);
        if (list) {
          this.filesDeal(list, currPath, spaceId);
        }
      }
    },
    // 拖拽上传
    async dropFile(currPath: string, files: UploadFile[], spaceId: string) {
      if (IS_BROWSER) {
        this.filesDeal(files, currPath, spaceId);
      } else {
        const list = await this.listFolderInPC(files.map(item => item.path));
        if (list) {
          this.filesDeal(list, currPath, spaceId);
        }
      }
    },
    async listFolderInPC(filePaths: string[]) {
      let finalList: FileStats[] = [];
      const failInfo: FailInfo = { list: [], type: 'upload' };
      const { validPaths, failPaths } = methods.checkLocalPathInvalid(filePaths);
      failInfo.list.push(...failPaths);
      if (validPaths.length > 0) {
        tips.loading(i18n_t('文件扫描中，请稍后查看'), 0);
        try {
          const { list } = await listFolders({ paths: validPaths, maxFiles: ConstantConfig.FILE_COUNT_LIMIT });
          finalList = list;
          tips.closeAll();
          return finalList;
        } catch (error: any) {
          // TODO 总数超过上限，这里的 path如何展示
          validPaths.forEach(item => {
            failInfo.list.push({
              path: item,
              error: error.code,
              msg: errorCodeMap[error.code as string],
            });
          });
          ipc.emit(ipc.RENDERER_EVENTS_MAP.ERRINFO_SHOW, failInfo);
        }
      }
    },
    // 选择文件上传
    handleFileInput(event: Event, el: HTMLInputElement, currPath: string, spaceId: string) {
      const { files } = el;
      this.filesDeal(files, currPath, spaceId);
      // eslint-disable-next-line no-param-reassign
      el.value = '';
    },
    // 选择文件夹
    handleDirectoryInput(event: Event, el: HTMLInputElement, currPath: string, spaceId: string) {
      const { files } = el;
      this.filesDeal(files, currPath, spaceId);
      // eslint-disable-next-line no-param-reassign
      el.value = '';
    },
    async checkIfUpgrade(size: number, spaceId: string) {
      try {
        const res = await smh.commonAPI.checkFileStorage({
          size,
          spaceId,
        });
        if (res.status === 204) return false;
      } catch (err: any) {
        if (err?.code === 'QuotaLimitReached' || err?.code === 'LibraryQuotaLimitReached') {
          // 判断是不是比可用的空间大
          if (routerName === 'collaborationSpaceList' || routerName === 'collaborationSpace') {
            reporter.report('value_added', {
              space_show: 'groups_upload_copy_fixed_popup',
            });
            openTeamPayDialog(PAY_SOURCE.UPLOAD_FILES, {
              isCollaboration: true,
              collaborationRole: vm.$route.params.isOwner,
            });
          } else {
            reporter.report('value_added', {
              space_show: 'upload_copy_file_fixed_popup',
            });
            openTeamPayDialog(PAY_SOURCE.UPLOAD_FILES);
          }
          return true;
        }
        return false;
      }
      return false;
    },
    async filesDeal(
      files: any,
      currPath: string,
      spaceId: string,
      options?: {
        teamPathInfo?: TeamPathInfo;
        routerName?: string;
        showTransmission?: boolean;
      },
    ) {
      if (options?.routerName) {
        routerName = options.routerName;
      }
      // 将文件分类，用于文件夹处理
      const res = await this.filesClassify(files, currPath, spaceId, options?.teamPathInfo);
      const [fileMap] = [res.fileMap];
      const taskList = Object.keys(fileMap);
      if (!taskList.length) {
        tips.error(i18n_t('没有要上传的文件'));
        return;
      }
      let size = 0;
      // 计算fileMap中多个文件的大小，文件夹就可以直接拿到folderSize
      // 判断是不是文件夹
      taskList.forEach(item => {
        const folder = fileMap[item][0];
        const folderSize = Number.isNaN(folder.folderSize) ? folder.body?.folderSize : folder.folderSize;
        size += Number(folderSize);
      });

      // 判断是否需要升级
      if (await methods.checkIfUpgrade(size, spaceId)) return;
      this.handleUpload(fileMap, [], currPath, spaceId);

      // 处理上报
      let fileNameList: string[] = [];
      const fileArr = Object.values(fileMap);
      fileArr.forEach((item: any) => {
        fileNameList = fileNameList.concat(item.map((file: any) => file.name));
      });
      const isBatch = fileNameList.length > 1;
      reporter.report('upload', {
        upload_location: routerName,
        upload_mode: isBatch ? 'batch_upload' : 'single_upload',
        uploadFileNames: fileNameList,
      });
    },
    /**
     * 文件（夹）标准化
     * @param files 用户选择、拖拽的文件（夹）
     * @param currPath 网盘内文件路径 "我的文件夹/新建文件夹"
     * @param spaceId 若没有值，则代表个人空间
     * @param _teamPathInfo
     */
    async filesClassify(files: any, currPath: string, spaceId: string, _teamPathInfo?: TeamPathInfo) {
      // teamPathInfo 若没有传递，则自行组装一个
      let teamPathInfo!: TeamPathInfo;
      if (_teamPathInfo) {
        teamPathInfo = _teamPathInfo;
      } else if (routerName === 'teamSpace') {
        teamPathInfo = storage.get(storage.StoreKey.TEAM_PATH) as TeamPathInfo;
      } else if (routerName === 'collaborationSpaceList') {
        const { spaceId, id, name, SpaceOrgId, path } = vm.$route.query;
        teamPathInfo = {
          path: (currPath as string).split('/'),
          spaceId: spaceId as string,
          orgId: +SpaceOrgId,
          groupId: +id,
          name: name as string,
        };
      }
      return filesClassifyAdapter(files, currPath, spaceId, routerName, teamPathInfo);
    },
    /**
     * 检查远端是否存在相同的文件或文件夹
     * 不存在时会返回错误码404
     */
    async checkRemotePathExist({ spaceId, path, isDir }: { spaceId: string; path: string; isDir: boolean }) {
      let existFolder = false;
      let existFile = false;
      let error = null;
      let isDifferentTypeConfilct = false;
      // 先检查是否存在同名文件,存在就不用检查同名文件夹
      try {
        await smh.commonAPI.inspectFile({
          spaceId,
          path,
        });
        existFile = true;
        isDifferentTypeConfilct = isDir;
        return {
          isDifferentTypeConfilct,
        };
      } catch (e: any) {
        error = e;
      }
      // 再检查是否存在同名文件夹
      try {
        await smh.commonAPI.inspectDirectory({
          spaceId,
          dirPath: path,
        });
        existFolder = true;
        isDifferentTypeConfilct = !isDir;
        return {
          isDifferentTypeConfilct,
        };
      } catch (e: any) {
        error = e;
      }
      // 远端不存在同名文件和文件夹,交给handleConflict来catch处理
      if (!existFile && !existFolder) {
        throw error;
      }
      return {
        isDifferentTypeConfilct,
      };
    },
    // 检查路径是否存在
    async checkIfPathExist(
      fileMap: Record<string, FileInfo[]>,
      currPath: string,
      spaceId: string,
    ): Promise<boolean | Record<string, string>> {
      const folderAndFiles = Object.keys(fileMap).map(key => {
        // 短路径排在长路径前
        fileMap[key] = fileMap[key].sort((fileA, fileB) => fileA.remotePath.length - fileB.remotePath.length);
        if (fileMap[key].length === 1) {
          return fileMap[key][0];
        }
        return fileMap[key].find(item => item.name === key) as FileInfo;
      });
      let uploadOperate = '';
      let sameOperate = false;
      try {
        await async.eachOfLimit<FileInfo>(folderAndFiles, 1, (item, index, next) => {
          if (!item) {
            next();
            return;
          }
          const handleConflict = async () => {
            try {
              /**
               * 检查远端对应路径是否有文件或文件夹存在，出现冲突弹窗询问
               * isDifferentTypeConfilct 表示当前上传与远端冲突类型不同，比如当前文件与远端文件夹冲突,弹窗里只能选择重命名
               * */
              const { isDifferentTypeConfilct } = await this.checkRemotePathExist({
                spaceId,
                path: item.remotePath,
                isDir: item.isDir,
              });

              // 文件/文件夹远端有冲突，需要弹窗询问
              try {
                if (!sameOperate) {
                  // 若一直没勾选后续行为统一处理，则一直弹窗询问
                  const { isSame, operate } = await openConflictDialog(
                    item,
                    folderAndFiles.slice(+index + 1),
                    isDifferentTypeConfilct,
                  );
                  sameOperate = isSame;
                  uploadOperate = operate;
                  reporter.report('upload', {
                    overlap: uploadOperate,
                    upload_location: routerName,
                  });
                }
                switch (uploadOperate) {
                  case 'skip':
                    delete fileMap[item.name];
                    next();
                    break;
                  case 'overwrite':
                    item.conflictResolutionStrategy = uploadOperate;
                    next();
                    break;
                  case 'rename':
                    if (!item.isDir) {
                      item.conflictResolutionStrategy = uploadOperate;
                      next();
                    } else {
                      smh.commonAPI
                        .createDirectory({
                          dirPath: item.remotePath,
                          conflictResolutionStrategy: 'rename',
                          spaceId,
                        })
                        .then(({ data }) => {
                          const newRemoteFolder = data.path.join('/');
                          const newRootFolderName = currPath
                            ? newRemoteFolder.substr(currPath.length + 1)
                            : newRemoteFolder;
                          fileMap[item.name].forEach(file => {
                            file.rootFolderName = newRootFolderName;
                            const oldRemoteRootFolder = file.remoteRootFolder;
                            file.remoteRootFolder = getConcatDirPath(currPath, newRootFolderName);
                            if (file.remoteFolder !== currPath) {
                              file.remoteFolder = file.remoteFolder.replace(oldRemoteRootFolder, file.remoteRootFolder);
                              file.remotePath = getConcatDirPath(file.remoteFolder, file.name);
                            } else {
                              file.remotePath = file.remoteRootFolder;
                            }
                          });
                          fileMap[newRootFolderName] = fileMap[item.name];
                          delete fileMap[item.name];
                          next();
                        })
                        .catch(err => {
                          next(err);
                        });
                    }
                    break;
                  default:
                    break;
                }
              } catch (error) {
                next(error as Error);
              }
            } catch (err: any) {
              if (err.status === 404) {
                // 文件/文件夹远端无冲突，不需要弹窗
                if (item.isDir) {
                  smh.commonAPI
                    .createDirectory({
                      spaceId,
                      dirPath: item.remotePath,
                    })
                    .then(() => next())
                    .catch(err => next(err));
                } else {
                  next();
                }
              } else {
                next(err);
              }
            }
          };
          handleConflict();
        });
        const hasFiles = !!Object.keys(fileMap).length;
        // 文件夹上传成功后需要更新当前列表页面
        hasFiles && ipc.emit(ipc.RENDERER_EVENTS_MAP.COMMAND_REFRESH);
        return hasFiles;
      } catch (error: any) {
        if (!error.toString().includes('stop')) {
          tips.error(error as MsgError);
        } else {
          reporter.report('upload', {
            overlap: 'cancel',
          });
        }
        return false;
      }
    },
    async handleUpload(fileMap: Record<string, FileInfo[]>, folders: string[], currPath: string, spaceId: string) {
      // 1. 检查上传的路径是否存在相同文件夹/文件
      const canContinue = await this.checkIfPathExist(fileMap, currPath, spaceId);
      if (!canContinue) return;
      this.uploadFiles(fileMap);

      // // 1. 创建所有文件夹
      // if (filderfolders.length > 0 && filderfolders[0]?.length > 0) {
      //   const folderNameChangeMap = await this.checkIfPathExist(folders, currPath, spaceId);
      //   if (typeof folderNameChangeMap !== 'boolean') {
      //     // 2. 上传文件
      //     this.uploadFiles(fileMap, folderNameChangeMap);
      //   }
      // } else {
      //   // 3. 无需要创建的文件夹，直接上传文件
      //   this.uploadFiles(fileMap, {});
      // }
      this.finishUploadGuide();
    },
    async uploadFiles(fileMap: Record<string, FileInfo[]>) {
      // 加入上传列表中
      const tasks: Task<FileInfo>[] = await handleUploadFilesToTask(fileMap);
      const taskAvailable = (task: Task<FileInfo>) => task.getListLength() !== 0;
      async.forEachLimit(
        tasks,
        2,
        (task, next) => {
          // 任务内部的list如果被全部过滤掉了，就不进行addTask了，直接下一个
          if (taskAvailable(task)) {
            uploadTaskManager.addTask(task);
          }
          next();
        },
        () => {
          uploadTaskManager.start();
        },
      );
      ipc.emit(ipc.RENDERER_EVENTS_MAP.WEB_TRANS_SHOW, {
        show: IS_BROWSER,
        tab: 'upload',
      });
      !IS_BROWSER && tips.info(i18n_t('已添加至传输列表'));
    },
    async finishUploadGuide() {
      // 查看新手引导是否有对应数据，如果没有，对其进行对应上传相关的更新
      if (routerName === 'personalSpace') {
        guideStepMethods.monitoringStepsDone('personalSpace', 0);
      }
    },
  };
  return {
    methods,
  };
}
